import classnames from "classnames"

import FilterIcon from "shared/components/icons/filter_icon"
import DelayIcon from "shared/components/icons/delay_icon"
import { creditLabel } from "./utils/credits"
import { verifyIntendedPromiseRejection } from "./utils/modal_promise"
import { cancellationError } from "./utils/error_map"

import styles from "./diy_tanda_selected.module.scss"
import { Service } from "./utils/interfaces"
import { MouseEventHandler } from "react"

import { tierBadge } from "shared/utils/tiers"
import sparkleIcon from "images/icons/icon-sparkle-white.svg?raw"

interface Props {
  canEdit: boolean
  color?: string
  showProFeatures?: boolean
  label: "if" | "with" | "when" | "wait" | "then" | "and"
  onDeleteClick: (dataConfirmation: () => Promise<void>) => Promise<void>
  onEditClick: () => Promise<void>
  liveChannelId?: string | undefined
  readOnly: boolean
  service: Service
  stepName: string | undefined
  stepTier: "free" | "intermediate_pro" | "pro"
  credits?: number
  disabled?: boolean
  usesAIActionAssist?: boolean
  warnings?: boolean
}

export default function DIYTandaSelected({
  canEdit,
  label,
  onDeleteClick,
  onEditClick,
  liveChannelId,
  readOnly,
  service,
  showProFeatures = false,
  stepName,
  stepTier,
  credits,
  disabled = false,
  usesAIActionAssist = false,
  warnings,
}: Props) {
  const isFilter = label === "when"
  const isDelay = label === "wait"

  const serviceImage = () => {
    if (isFilter) {
      return <FilterIcon title="filter icon" />
    } else if (isDelay) {
      return <DelayIcon title="delay icon" />
    } else {
      return <img width="20" height="20" src={service.image} alt={`${service.name} icon`} title={service.name} />
    }
  }

  const highlighted = ["if", "then"].includes(label)
  const blockClass = classnames(styles["tanda-selected-block"], {
    [styles["with-credits"]]: credits !== undefined,
    [styles.highlighted]: highlighted,
    [styles["with-warnings"]]: warnings,
    [styles.clickable]: (canEdit || isFilter) && !disabled,
    [styles.disabled]: disabled,
  })
  const blockStyle = { backgroundColor: disabled ? '#999' : service.brand_color }

  const dataConfirmation = () => {
    let message: string
    if (label === "if") {
      message = showProFeatures
        ? "If you’ve used any of this trigger’s ingredients in queries, filters or actions you will have to remove those ingredients before saving this Applet."
        : "If you’ve used any of this trigger’s ingredients in your action you will have to remove those ingredients before saving this Applet."
    } else if (label === "with") {
      message =
        "If you’ve used any of this query’s ingredients in a filter or actions you will have to remove those ingredients before saving this Applet."
    } else if (showProFeatures && ["then", "and"].includes(label)) {
      message = "If you’ve used this action in a filter you will have to remove it from the filter before saving this Applet."
    }

    return new Promise<void>((resolve, reject) => {
      if (message && !window.confirm(message)) {
        reject(cancellationError)
      } else {
        resolve()
      }
    })
  }

  const wrapHandler = (handler: () => Promise<void>) => {
    return (e: MouseEventHandler<HTMLElement>) => {
      e.stopPropagation()
      verifyIntendedPromiseRejection(handler())
    }
  }

  const selectedLiveChannel = service.live_channels?.find(lc => lc.id == liveChannelId)

  return (
    <div className={styles["tanda-selected-container"]}>
      <section
        onClick={(canEdit || isFilter) && !disabled ? wrapHandler(onEditClick) : undefined}
        style={blockStyle}
        className={blockClass}
      >
        <span className={styles.label}>{label}</span>
        {serviceImage()}
        <div className={styles.step}>
          <span className={styles["step-name"]}>
            {stepName}
            {usesAIActionAssist && (
              <span className={styles["ai-action-assist"]} tabIndex={0} aria-label="This action uses AI Action assist">
                <span className={styles["ai-action-assist-icon"]} dangerouslySetInnerHTML={{ __html: sparkleIcon }} />
                <span className={styles["ai-action-assist-tooltip"]} role="tooltip">
                  This action uses AI Action assist
                </span>
              </span>
            )}
          </span>
          {service.allow_multiple_live_channels && (
            <span className={styles["step-multi-account-selected"]}>{selectedLiveChannel?.user_name_field}</span>
          )}
          {credits !== undefined && <span className={styles["credit-badge"]}>{creditLabel(credits)}</span>}
        </div>
      </section>

      {(canEdit || isFilter || !readOnly) && (
        <div
          className={classnames(styles["actions-row"], {
            [styles.highlighted]: highlighted,
            [styles["with-warnings"]]: warnings,
          })}
          style={blockStyle}
        >
          <div className={styles["actions-overlay"]}>
            {stepTier === "pro" || stepTier === "intermediate_pro" ? (
              <span dangerouslySetInnerHTML={{ __html: tierBadge(stepTier) }} />
            ) : (
              <span></span>
            )}
            <div className={styles.actions}>
              {(canEdit || isFilter) && (
                <button
                  className={classnames(styles.action, { [styles.disabled]: disabled })}
                  onClick={disabled ? undefined : wrapHandler(onEditClick)}
                  disabled={disabled}
                >
                  Edit
                </button>
              )}
              {!readOnly && (
                <button className={styles.action} onClick={wrapHandler(() => onDeleteClick(dataConfirmation))}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
